import type { SVGProps } from 'react'

/**
 * How every illustration below fills its slot.
 *
 * These are square (120x120) artwork dropped into card slots that are not
 * square — a 4:3 meal tile, a 1:1.04 chef poster. The SVG default,
 * `xMidYMid meet`, letterboxes: the drawing shrinks to fit and leaves bands of
 * the parent background above and below it. Sitting next to a cover-filled
 * photograph in the same row, that reads as an image that failed to centre,
 * which is exactly how the cards looked.
 *
 * `slice` scales to cover and centres on both axes, cropping the overflow —
 * the same behaviour as `object-fit: cover` on the photo it stands in for, so
 * a card looks right whether or not the kitchen has uploaded a photo yet.
 *
 * Applied before each component's {...props} spread, so a caller that wants
 * the whole drawing can still pass `preserveAspectRatio="xMidYMid meet"`.
 */
const COVER = 'xMidYMid slice'

export interface IllustrationProps extends SVGProps<SVGSVGElement> {
  size?: number | string
}

/**
 * Universal, gender-neutral culinary insignia for verified chefs.
 * Features an artisanal toque blanche, crossed kitchen utensils, fresh herbs,
 * and a warm medallion.
 */
export const ChefIllustration = ({ size = 64, className, ...props }: IllustrationProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio={COVER}
    className={className}
    {...props}
  >
    <rect width="100%" height="100%" fill="#faf6f0" />
    <circle cx="60" cy="60" r="46" fill="#f0e4d2" />
    <circle cx="60" cy="60" r="42" fill="#e8d5be" stroke="#d4be9f" strokeWidth="1.5" strokeDasharray="3 3" />

    {/* Crossed utensils (whisk & wooden spoon) */}
    <g transform="rotate(45 60 68)">
      <rect x="58" y="32" width="4" height="52" rx="2" fill="#b07844" />
      <ellipse cx="60" cy="32" rx="7" ry="10" fill="#b07844" stroke="#8a5322" strokeWidth="1.2" />
    </g>
    <g transform="rotate(-45 60 68)">
      <rect x="58" y="32" width="4" height="52" rx="2" fill="#7a8288" />
      <path
        d="M54 36C54 26 66 26 66 36C66 42 54 42 54 36Z"
        fill="none"
        stroke="#596167"
        strokeWidth="1.8"
      />
      <path
        d="M57 38C57 30 63 30 63 38"
        fill="none"
        stroke="#596167"
        strokeWidth="1.4"
      />
    </g>

    {/* Central Toque Blanche (Chef Hat) */}
    <path
      d="M38 52C32 40 34 22 50 16C56 12 64 12 70 16C86 22 88 40 82 52H38Z"
      fill="#ffffff"
      stroke="#17273E"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    <path
      d="M36 52H84V60C84 62 82 64 80 64H40C38 64 36 62 36 60V52Z"
      fill="#E61445"
      stroke="#17273E"
      strokeWidth="2"
    />
    <rect x="40" y="55" width="40" height="2" rx="1" fill="#f5c242" />

    {/* Pleats on toque */}
    <path d="M48 20V48" stroke="#e8ded1" strokeWidth="2" strokeLinecap="round" />
    <path d="M60 16V48" stroke="#e8ded1" strokeWidth="2" strokeLinecap="round" />
    <path d="M72 20V48" stroke="#e8ded1" strokeWidth="2" strokeLinecap="round" />

    {/* Fresh basil/parsley garnish */}
    <path d="M48 84C42 78 46 72 52 74C56 75 58 80 56 86C52 86 49 85 48 84Z" fill="#388e3c" />
    <path d="M72 84C78 78 74 72 68 74C64 75 62 80 64 86C68 86 71 85 72 84Z" fill="#2e7d32" />
    <circle cx="60" cy="80" r="3" fill="#c62828" />
  </svg>
)

/**
 * Universal Bakery / Patisserie insignia.
 */
export const BakeryIllustration = ({ size = 64, className, ...props }: IllustrationProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio={COVER}
    className={className}
    {...props}
  >
    <rect width="100%" height="100%" fill="#fdf8f2" />
    <circle cx="60" cy="60" r="44" fill="#fcebd4" />

    {/* Rolling pin */}
    <g transform="rotate(-30 60 60)">
      <rect x="24" y="56" width="72" height="8" rx="4" fill="#d49b5c" stroke="#8a5322" strokeWidth="1.5" />
      <rect x="14" y="58" width="10" height="4" rx="2" fill="#a36e35" />
      <rect x="96" y="58" width="10" height="4" rx="2" fill="#a36e35" />
    </g>

    {/* Golden Croissant / Bread */}
    <path
      d="M32 72C32 54 50 42 60 42C70 42 88 54 88 72C88 80 80 84 60 84C40 84 32 80 32 72Z"
      fill="#e59838"
      stroke="#8a4b08"
      strokeWidth="2.5"
    />
    <path d="M44 56C50 50 60 48 70 54" stroke="#c27415" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M40 68C48 64 64 64 76 68" stroke="#c27415" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
)

/**
 * Universal Grill & BBQ insignia.
 */
export const GrillStationIllustration = ({ size = 64, className, ...props }: IllustrationProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio={COVER}
    className={className}
    {...props}
  >
    <rect width="100%" height="100%" fill="#fcf4f2" />
    <circle cx="60" cy="60" r="44" fill="#fae2dc" />

    {/* Glowing charcoal base */}
    <ellipse cx="60" cy="76" rx="36" ry="14" fill="#2d120e" stroke="#1a0805" strokeWidth="2" />
    <circle cx="48" cy="76" r="4" fill="#e53935" />
    <circle cx="62" cy="78" r="5" fill="#ff7043" />
    <circle cx="74" cy="75" r="3.5" fill="#ffa726" />

    {/* Grill grid */}
    <line x1="28" y1="72" x2="92" y2="72" stroke="#78909c" strokeWidth="2" />
    <line x1="32" y1="76" x2="88" y2="76" stroke="#78909c" strokeWidth="2" />
    <line x1="42" y1="64" x2="42" y2="84" stroke="#78909c" strokeWidth="1.5" />
    <line x1="60" y1="64" x2="60" y2="86" stroke="#78909c" strokeWidth="1.5" />
    <line x1="78" y1="64" x2="78" y2="84" stroke="#78909c" strokeWidth="1.5" />

    {/* Suya skewer across grill */}
    <g transform="rotate(-15 60 54)">
      <line x1="20" y1="54" x2="100" y2="54" stroke="#d7ccc8" strokeWidth="2" strokeLinecap="round" />
      <rect x="36" y="46" width="12" height="16" rx="3" fill="#8d2b1b" stroke="#5d160c" strokeWidth="1.5" />
      <rect x="52" y="44" width="14" height="20" rx="3" fill="#a33624" stroke="#5d160c" strokeWidth="1.5" />
      <rect x="70" y="47" width="12" height="15" rx="3" fill="#8d2b1b" stroke="#5d160c" strokeWidth="1.5" />
      <circle cx="42" cy="52" r="1.5" fill="#ffb74d" />
      <circle cx="58" cy="50" r="1.5" fill="#ffb74d" />
      <circle cx="60" cy="58" r="1.5" fill="#ffb74d" />
      <circle cx="76" cy="54" r="1.5" fill="#ffb74d" />
    </g>

    {/* Rising smoke wisps */}
    <path d="M52 38Q48 30 52 24Q56 18 52 12" stroke="#b0bec5" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.7" />
    <path d="M68 40Q72 32 68 26Q64 20 68 14" stroke="#b0bec5" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.7" />
  </svg>
)

/**
 * Detailed Nigerian pastry / bakery box illustration.
 */
export const PastryBoxIllustration = ({ size = 64, className, ...props }: IllustrationProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio={COVER}
    className={className}
    {...props}
  >
    <rect width="100%" height="100%" fill="#fcf6ec" />
    <circle cx="60" cy="62" r="44" fill="#faebd7" />

    {/* Kraft box base */}
    <path d="M22 58L60 74L98 58L60 42L22 58Z" fill="#d49b5c" stroke="#8a5322" strokeWidth="2" />
    <path d="M22 58V84L60 100V74L22 58Z" fill="#b87e40" stroke="#8a5322" strokeWidth="2" />
    <path d="M98 58V84L60 100V74L98 58Z" fill="#a36e35" stroke="#8a5322" strokeWidth="2" />

    {/* Golden Pastry / Roll inside */}
    <ellipse cx="60" cy="56" rx="20" ry="10" fill="#f5af42" stroke="#b86a14" strokeWidth="1.5" />
    <path d="M48 54C54 50 66 50 72 54" stroke="#b86a14" strokeWidth="1.5" strokeLinecap="round" />

    {/* Celebratory Red Ribbon */}
    <path d="M60 42L60 100" stroke="#c62828" strokeWidth="4" />
    <path d="M22 58L98 58" stroke="#c62828" strokeWidth="4" />
    <ellipse cx="52" cy="40" rx="7" ry="5" fill="#d32f2f" stroke="#8b0000" strokeWidth="1" />
    <ellipse cx="68" cy="40" rx="7" ry="5" fill="#d32f2f" stroke="#8b0000" strokeWidth="1" />
    <circle cx="60" cy="41" r="3.5" fill="#b71c1c" />
  </svg>
)

/**
 * Detailed Smoky Jollof Rice illustration.
 */
export const JollofRiceIllustration = ({ size = 64, className, ...props }: IllustrationProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio={COVER}
    className={className}
    {...props}
  >
    <rect width="100%" height="100%" fill="#faf2ed" />
    <circle cx="60" cy="62" r="46" fill="#f7ded2" />

    {/* Ceramic serving plate */}
    <ellipse cx="60" cy="68" rx="46" ry="32" fill="#ffffff" stroke="#e0d0c5" strokeWidth="2" />
    <ellipse cx="60" cy="68" rx="38" ry="24" fill="#f7ede6" />

    {/* Smoky Red Jollof mound */}
    <ellipse cx="58" cy="68" rx="30" ry="18" fill="#d84315" />
    <path
      d="M32 68C32 54 44 46 58 46C72 46 84 54 84 68C84 76 72 82 58 82C44 82 32 76 32 68Z"
      fill="#e64a19"
      stroke="#bf360c"
      strokeWidth="1.5"
    />

    {/* Grains */}
    <circle cx="50" cy="56" r="1.5" fill="#ff8a65" />
    <circle cx="62" cy="54" r="1.5" fill="#ff8a65" />
    <circle cx="70" cy="60" r="1.5" fill="#ff8a65" />
    <circle cx="56" cy="64" r="1.5" fill="#ff8a65" />
    <circle cx="44" cy="66" r="1.5" fill="#ff8a65" />
    <circle cx="66" cy="70" r="1.5" fill="#ffab91" />

    {/* Fried Plantains (Dodo) */}
    <path d="M38 72C40 66 48 66 50 72C50 75 42 77 38 72Z" fill="#ffb300" stroke="#e65100" strokeWidth="1.2" />
    <path d="M46 76C48 70 56 70 58 76C58 79 50 81 46 76Z" fill="#ffb300" stroke="#e65100" strokeWidth="1.2" />

    {/* Grilled chicken wing & bay leaf */}
    <path d="M68 54C74 48 82 54 80 62C78 66 70 66 68 54Z" fill="#795548" stroke="#4e342e" strokeWidth="1.5" />
    <path d="M58 42C56 36 62 34 64 40C64 42 60 44 58 42Z" fill="#388e3c" />

    {/* Steam */}
    <path d="M54 32Q50 24 54 18Q58 12 54 6" stroke="#d84315" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity="0.6" />
    <path d="M64 34Q68 26 64 20Q60 14 64 8" stroke="#d84315" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity="0.6" />
  </svg>
)

/**
 * Detailed Egusi & African Soup Pot illustration.
 */
export const SoupPotIllustration = ({ size = 64, className, ...props }: IllustrationProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio={COVER}
    className={className}
    {...props}
  >
    <rect width="100%" height="100%" fill="#fbf5ee" />
    <circle cx="60" cy="62" r="44" fill="#f5e4cf" />

    {/* Heavy Cast-Iron Dutch Oven */}
    <ellipse cx="60" cy="74" rx="42" ry="18" fill="#2d1c14" />
    <path
      d="M20 54H100V74C100 88 82 98 60 98C38 98 20 88 20 74V54Z"
      fill="#3d261b"
      stroke="#1f110b"
      strokeWidth="2.5"
    />
    <path d="M12 58H20V66H12C9.8 66 8 64.2 8 62C8 59.8 9.8 58 12 58Z" fill="#1f110b" />
    <path d="M108 58H100V66H108C110.2 66 112 64.2 112 62C112 59.8 110.2 58 108 58Z" fill="#1f110b" />

    {/* Soup inside */}
    <ellipse cx="60" cy="54" rx="38" ry="14" fill="#e67e22" stroke="#b05206" strokeWidth="1.5" />
    <circle cx="48" cy="52" r="4" fill="#2e7d32" />
    <circle cx="70" cy="54" r="5" fill="#1b5e20" />
    <circle cx="58" cy="58" r="3.5" fill="#2e7d32" />
    <rect x="42" y="52" width="7" height="6" rx="2" fill="#d35400" />
    <rect x="66" y="50" width="8" height="6" rx="2" fill="#ba4a00" />

    {/* Wooden ladle */}
    <rect x="74" y="24" width="4" height="34" rx="2" transform="rotate(25 74 24)" fill="#a0522d" />

    {/* Steam */}
    <path d="M46 36Q42 28 46 22Q50 16 46 10" stroke="#e67e22" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.65" />
    <path d="M58 34Q62 26 58 20Q54 14 58 8" stroke="#e67e22" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.65" />
  </svg>
)

/**
 * Detailed Suya & Charcoal Skewers illustration.
 */
export const GrillSuyaIllustration = ({ size = 64, className, ...props }: IllustrationProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio={COVER}
    className={className}
    {...props}
  >
    <rect width="100%" height="100%" fill="#faf0ee" />
    <circle cx="60" cy="62" r="44" fill="#f5d7cf" />

    {/* Wooden board */}
    <rect x="22" y="34" width="76" height="64" rx="6" fill="#e0cbaf" stroke="#a48c6e" strokeWidth="2" />
    <rect x="26" y="38" width="68" height="56" rx="4" fill="#f2e3ce" />

    {/* Skewer 1 */}
    <line x1="20" y1="88" x2="100" y2="40" stroke="#bcaaa4" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="44" cy="74" r="7" fill="#8d2b1b" stroke="#4e130a" strokeWidth="1.2" />
    <circle cx="58" cy="65" r="8" fill="#a33624" stroke="#4e130a" strokeWidth="1.2" />
    <circle cx="74" cy="56" r="7" fill="#8d2b1b" stroke="#4e130a" strokeWidth="1.2" />

    {/* Skewer 2 */}
    <line x1="24" y1="96" x2="104" y2="48" stroke="#bcaaa4" strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="48" cy="82" r="7" fill="#7a2416" stroke="#4e130a" strokeWidth="1.2" />
    <circle cx="62" cy="73" r="8" fill="#8d2b1b" stroke="#4e130a" strokeWidth="1.2" />
    <circle cx="78" cy="64" r="7" fill="#a33624" stroke="#4e130a" strokeWidth="1.2" />

    {/* Onions and tomato */}
    <ellipse cx="36" cy="50" rx="6" ry="4" fill="#ffffff" stroke="#8e24aa" strokeWidth="1.5" />
    <ellipse cx="84" cy="78" rx="6" ry="4" fill="#ffffff" stroke="#8e24aa" strokeWidth="1.5" />
    <circle cx="48" cy="46" r="5" fill="#e53935" stroke="#b71c1c" strokeWidth="1" />

    {/* Spices */}
    <circle cx="54" cy="68" r="1.5" fill="#ffb74d" />
    <circle cx="64" cy="62" r="1.5" fill="#ffb74d" />
    <circle cx="70" cy="70" r="1.5" fill="#ffb74d" />
  </svg>
)

/**
 * Detailed Celebration Cake illustration.
 */
export const CakeIllustration = ({ size = 64, className, ...props }: IllustrationProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio={COVER}
    className={className}
    {...props}
  >
    <rect width="100%" height="100%" fill="#fdf4f5" />
    <circle cx="60" cy="62" r="44" fill="#fce0e3" />

    {/* Cake stand */}
    <ellipse cx="60" cy="94" rx="44" ry="12" fill="#ffffff" stroke="#e0cfd1" strokeWidth="2" />

    {/* Bottom Tier */}
    <path
      d="M26 64H94V84C94 90 78 94 60 94C42 94 26 90 26 84V64Z"
      fill="#8b1c27"
      stroke="#520a11"
      strokeWidth="2"
    />
    <ellipse cx="60" cy="64" rx="34" ry="10" fill="#fff5f5" stroke="#520a11" strokeWidth="1.5" />

    {/* Top Tier */}
    <path
      d="M38 42H82V58C82 63 72 66 60 66C48 66 38 63 38 58V42Z"
      fill="#8b1c27"
      stroke="#520a11"
      strokeWidth="2"
    />
    <ellipse cx="60" cy="42" rx="22" ry="7" fill="#fff5f5" stroke="#520a11" strokeWidth="1.5" />

    {/* Frosting drips */}
    <path d="M38 44C40 48 44 48 46 44C48 49 52 49 54 44C56 50 60 50 62 44C64 49 68 49 70 44C72 49 76 49 78 44C80 48 82 46 82 44" stroke="#fff5f5" strokeWidth="3" strokeLinecap="round" />
    <path d="M26 66C30 72 36 72 40 66C44 73 50 73 54 66C58 74 64 74 68 66C72 73 78 73 82 66C86 72 90 72 94 66" stroke="#fff5f5" strokeWidth="3.5" strokeLinecap="round" />

    {/* Berries on top */}
    <circle cx="56" cy="38" r="3" fill="#c2185b" />
    <circle cx="64" cy="38" r="3.5" fill="#880e4f" />
    <path d="M60 34C58 30 62 28 64 32" stroke="#2e7d32" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)
